import { createContext, useContext, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { meApi, spaceApi } from '../services/api';
import { useSpaceId } from '../contexts/SpaceContext';
import type {
  Tool,
  ToolReference,
  CreateToolInput,
  UpdateToolInput,
  GroupedToolsResponse,
} from '../types';

// ---------------------------------------------------------------------------
// Space-scoped — used by every console tools page (under /console/spaces/:id)
// ---------------------------------------------------------------------------

function useToolApi() {
  const spaceId = useSpaceId();
  return useMemo(() => spaceApi(spaceId).tools, [spaceId]);
}

export function useSpaceTools() {
  const spaceId = useSpaceId();
  const api = useToolApi();
  return useQuery({
    queryKey: ['space', spaceId, 'tools'],
    queryFn: () => api.list(),
  });
}

export function useSpaceTool(toolId: string | undefined) {
  const spaceId = useSpaceId();
  const api = useToolApi();
  return useQuery({
    queryKey: ['space', spaceId, 'tools', toolId],
    queryFn: () => api.get(toolId!),
    enabled: !!toolId,
    staleTime: Infinity,
  });
}

export function useCreateSpaceTool() {
  const spaceId = useSpaceId();
  const api = useToolApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateToolInput) => api.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'tools'] });
    },
  });
}

export function useUpdateSpaceTool() {
  const spaceId = useSpaceId();
  const api = useToolApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ toolId, input }: { toolId: string; input: UpdateToolInput }) =>
      api.update(toolId, input),
    onSuccess: (data: Tool) => {
      queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'tools'] });
      queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'tools', data.toolId] });
    },
  });
}

export function useDeleteSpaceTool() {
  const spaceId = useSpaceId();
  const api = useToolApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (toolId: string) => api.delete(toolId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'tools'] });
    },
  });
}

/** Builds GroupedToolsResponse from a flat list of tools (client-side fallback). */
function groupToolsClientSide(tools: Tool[]): GroupedToolsResponse {
  const map = new Map<string, Tool[]>();
  for (const tool of tools) {
    const key = tool.group || 'other';
    const arr = map.get(key);
    if (arr) arr.push(tool);
    else map.set(key, [tool]);
  }
  const groups = Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([group, groupTools]) => ({
      group,
      groupSlug: groupTools.find((t) => t.groupSlug)?.groupSlug,
      tools: groupTools.sort(
        (a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999),
      ),
    }));
  return { groups };
}

export function useGroupedSpaceTools() {
  const spaceId = useSpaceId();
  const api = useToolApi();
  const grouped = useQuery({
    queryKey: ['space', spaceId, 'tools', 'grouped'],
    queryFn: () => api.grouped(),
    retry: false,
  });

  const useFallback = grouped.isError || (grouped.isSuccess && !grouped.data?.groups);

  const flatList = useQuery({
    queryKey: ['space', spaceId, 'tools'],
    queryFn: () => api.list(),
    enabled: useFallback,
  });

  if (useFallback) {
    return {
      ...flatList,
      data: flatList.data
        ? groupToolsClientSide(flatList.data.items || [])
        : undefined,
    };
  }

  return { ...grouped, data: grouped.data };
}

// ---------------------------------------------------------------------------
// Cross-space — used by Builder canvas, EntityView, etc.
// ---------------------------------------------------------------------------

export function useMeTools() {
  return useQuery({
    queryKey: ['me', 'tools'],
    queryFn: () => meApi.tools(),
  });
}

// ---------------------------------------------------------------------------
// Default exports — space-scoped is the right default for most callers.
// ---------------------------------------------------------------------------

export const useTools = useSpaceTools;
export const useTool = useSpaceTool;
export const useCreateTool = useCreateSpaceTool;
export const useUpdateTool = useUpdateSpaceTool;
export const useDeleteTool = useDeleteSpaceTool;
export const useGroupedTools = useGroupedSpaceTools;

// ---------------------------------------------------------------------------
// Tool-name context — fed from the agent's embedded `tools` array so that
// display-name resolution is synchronous (no extra API calls).
// ---------------------------------------------------------------------------

/** Map of toolId → display name, built from Agent.tools[]. */
export type ToolNameMap = ReadonlyMap<string, string>;

const ToolNameContext = createContext<ToolNameMap>(new Map());

export const ToolNameProvider = ToolNameContext.Provider;

/**
 * Build a ToolNameMap from the ToolReference[] returned by the agent API.
 * Memoised so the map reference is stable across re-renders.
 */
export function useToolNameMap(tools: ToolReference[] | undefined): ToolNameMap {
  return useMemo(() => {
    const m = new Map<string, string>();
    if (tools) {
      for (const t of tools) m.set(t.toolId, t.name);
    }
    return m;
  }, [tools]);
}

/** Friendly display names for known internal/built-in tools. */
const BUILTIN_DISPLAY_NAMES: Record<string, string> = {
  activate_skill: 'Activate Skill',
  deactivate_skill: 'Deactivate Skill',
  ask_user: 'Ask User',
  resolve_datetime: 'Resolve Date/Time',
  __subagent_spawn: 'Dispatch Sub-Agents',
  __subagent_run: 'Sub-Agent Run',
};

/**
 * Resolves a single tool name or ID to its display name.
 *
 * Reads from the ToolNameContext (populated by ConversationThread from the
 * agent's embedded `tools` array). Falls back to the raw value when the
 * context has no entry (e.g. context not yet provided, or unknown tool).
 */
export function useToolDisplayName(nameOrId: string, backendDisplayName?: string): string {
  const map = useContext(ToolNameContext);
  return backendDisplayName || map.get(nameOrId) || BUILTIN_DISPLAY_NAMES[nameOrId] || nameOrId;
}
